import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import { CSVFile, CSVRow, processCSV } from "@redotech/csv/csv-parser";
import { Currency } from "@redotech/money/currencies";
import { OrderImportField } from "@redotech/redo-fulfillment-common/import-schema";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { CreateFulfillmentOrder } from "@redotech/redo-fulfillment-operations-common/create-fulfillment-order";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { ProductInfo } from "@redotech/redo-product-common/product";
import { parallelForEach } from "@redotech/util/async-iterator";
import {
  collectParseResults,
  FieldToColumnMap,
  ParseResult,
} from "./validate-fulfillment-orders";

// CSV SCHEMA:
// Backer Number, Backer UID, Backer Name, Email, Shipping Country, Shipping Amount,
// Reward Title, Backing Minimum, Reward ID, Bonus Support, Pledge Amount, Pledged At (UTC),
// Late Pledge, Fulfillment Status, Pledged Status, Notes,
// Product columns with format: (SKU) Product Name - each contains quantity
// Survey Response (UTC), Shipping Name, Shipping Address 1, Shipping Address 2,
// Shipping City, Shipping State, Shipping Postal Code, Shipping Country Name,
// Shipping Country Code, Shipping Phone Number

interface ProductHeader {
  columnName: string;
  displayName: string;
  sku: string | null;
}

interface PopulatedProduct {
  id?: string;
  name?: string;
  image?: { url?: string };
  variant?: {
    id?: string;
    name?: string;
    sku?: string;
    price?: string;
    weightGrams?: number | null;
  };
}

const kickstarterMetadataColumns = new Set([
  "Backer Number",
  "Backer UID",
  "Backer Name",
  "Email",
  "Shipping Country",
  "Shipping Amount",
  "Reward Title",
  "Backing Minimum",
  "Reward ID",
  "Bonus Support",
  "Pledge Amount",
  "Pledged At (UTC)",
  "Late Pledge",
  "Fulfillment Status",
  "Pledged Status",
  "Notes",
  "Survey Response (UTC)",
  "Shipping Name",
  "Shipping Address 1",
  "Shipping Address 2",
  "Shipping City",
  "Shipping State",
  "Shipping Postal Code",
  "Shipping Country Name",
  "Shipping Country Code",
  "Shipping Phone Number",
]);

export async function parseKickstarterCsv(
  file: File,
  searchProducts: (sku: string) => Promise<{ products: ProductInfo[] }>,
): Promise<ParseResult> {
  const data: CSVFile = await processCSV(file);

  if (data.rows.length === 0) {
    return { orders: [], parseErrors: [] };
  }

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const productHeaders = extractProductHeaders(Object.keys(data.rows[0]));
  const productLookupMap = new Map<string, PopulatedProduct | null>();

  const skusToLookup = productHeaders
    .map((header) => header.sku)
    .filter((sku): sku is string => sku !== null);

  await parallelForEach(skusToLookup, 5, async (sku) => {
    try {
      const { products } = await searchProducts(sku);
      const populatedItem = getPopulatedItemFromSku(sku, products);
      productLookupMap.set(sku, populatedItem);
    } catch {
      productLookupMap.set(sku, null);
    }
  });

  return collectParseResults(
    data.rows,
    async (row) => buildRow(row, productHeaders, productLookupMap),
    (row, i) => row["Backer Number"] || `Row ${i + 1}`,
  );
}

function extractProductHeaders(headers: string[]): ProductHeader[] {
  return headers
    .filter((header) => !kickstarterMetadataColumns.has(header))
    .filter((header) => header.match(/^\([^)]*\)/))
    .map((header) => {
      const skuMatch = header.match(/^\(\s*([^)]*?)\s*\)/);
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const sku = skuMatch && skuMatch[1].trim() ? skuMatch[1].trim() : null;
      const displayName = header.replace(/^\([^)]*\)\s*/, "").trim();
      return { columnName: header, displayName: displayName || header, sku };
    });
}

function cleanPhoneNumber(phoneNumber: string): string | null {
  if (!phoneNumber) {
    return null;
  }

  const digitsOnly = phoneNumber.replace(/\D/g, "");
  if (digitsOnly.length > 10) {
    return digitsOnly.slice(-10);
  }
  if (digitsOnly.length === 10) {
    return digitsOnly;
  }
  return null;
}

async function buildRow(
  row: CSVRow,
  productHeaders: ProductHeader[],
  productLookupMap: Map<string, PopulatedProduct | null>,
): Promise<CreateFulfillmentOrder> {
  // Extract address information from Kickstarter-specific columns
  const name = row["Shipping Name"] || "";
  const email = row.Email || "";
  const address1 = row["Shipping Address 1"] || "";
  const address2 = row["Shipping Address 2"] || "";
  const city = row["Shipping City"] || "";
  const state = row["Shipping State"] || "";
  const zip = row["Shipping Postal Code"] || "";
  const phone = cleanPhoneNumber(row["Shipping Phone Number"] || "");
  const backerNumber = row["Backer Number"] || "";
  const orderNumber = `KS-${Math.floor(Math.random() * 1_000_000_000)}`;
  const buyerNote = row.Notes || "";

  if (!name || !address1 || !city || !state || !zip) {
    throw new Error("Failed to parse row - missing required address fields");
  }

  // Parse products based on dynamic headers
  const items: FulfillmentOrderLineItem[] = [];

  for (const productHeader of productHeaders) {
    const quantityStr = row[productHeader.columnName]?.toString().trim();

    if (quantityStr && quantityStr !== "0") {
      const quantity = parseInt(quantityStr, 10);

      if (quantity > 1000) {
        throw new Error("Failed to parse row - quantity too high");
      }

      if (!isNaN(quantity) && quantity > 0) {
        // Use the extracted SKU if available, otherwise use the display name
        const skuToSearch = productHeader.sku ?? "";
        const productName = productHeader.displayName;
        // Get the pre-fetched product data from the lookup map
        const populatedItem = productLookupMap.get(skuToSearch) || null;

        items.push({
          sku: skuToSearch,
          quantity,
          unitPrice: {
            amount: populatedItem?.variant?.price ?? "0",
            currency: Currency.USD,
          },
          totalPrice: {
            amount: (
              parseFloat(populatedItem?.variant?.price ?? "0") * quantity
            ).toString(),
            currency: Currency.USD,
          },
          imageUrl: populatedItem?.image?.url,
          variantId: populatedItem?.variant?.id ?? skuToSearch,
          productId: populatedItem?.id ?? productName,
          vendor: "",
          requiresShipping: true,
          externalId: skuToSearch,
          customAttributes: [],
          totalQuantity: quantity,
          unfulfilledQuantity: quantity,
          weight: {
            unit: WeightUnit.G,
            value: populatedItem?.variant?.weightGrams ?? 0,
          },
          lineItemId: skuToSearch,
          title: populatedItem?.name ?? productHeader.displayName,
          productTitle: populatedItem?.name ?? productHeader.displayName,
          variantTitle:
            populatedItem?.variant?.name ?? productHeader.displayName,
          unitDiscountedPrice: {
            amount: populatedItem?.variant?.price ?? "0",
            currency: Currency.USD,
          },
          totalDiscountedPrice: {
            amount: (
              parseFloat(populatedItem?.variant?.price ?? "0") * quantity
            ).toString(),
            currency: Currency.USD,
          },
          totalTax: { amount: "0", currency: Currency.USD },
          hsCode: null,
        });
      }
    }
  }

  const data: CreateFulfillmentOrder = {
    address: {
      countryCode: RedoShopifyCountryCode.Us,
      name,
      email,
      address1,
      address2,
      city,
      province: state,
      zip,
      phone,
    },
    orderInfo: {
      orderNumber,
      orderDate: new Date(),
      paidDate: new Date(),
      shippingPaid: { amount: "0", currency: Currency.USD },
      taxPaid: { amount: "0", currency: Currency.USD },
      totalPaid: { amount: "0", currency: Currency.USD },
      items,
      internalNote: `Kickstarter Backer: ${backerNumber}`,
      orderNote: buyerNote,
    },
  };

  return data;
}

const getPopulatedItemFromSku = (sku: string, products: ProductInfo[]) => {
  const matchedProduct = products.find((product) =>
    product.variants.some((variant) => variant.sku === sku),
  );
  const matchedVariant = matchedProduct?.variants.find(
    (variant) => variant.sku === sku,
  );
  return { ...matchedProduct, variant: matchedVariant };
};

export const kickstarterColumnMap: FieldToColumnMap = {
  [OrderImportField.RECIPIENT_FULL_NAME]: "Shipping Name",
  [OrderImportField.ADDRESS_LINE_1]: "Shipping Address 1",
  [OrderImportField.ADDRESS_LINE_2]: "Shipping Address 2",
  [OrderImportField.CITY]: "Shipping City",
  [OrderImportField.STATE]: "Shipping State",
  [OrderImportField.POSTAL_CODE]: "Shipping Postal Code",
  [OrderImportField.BUYER_PHONE]: "Shipping Phone Number",
  [OrderImportField.BUYER_EMAIL]: "Email",
  [OrderImportField.ORDER_NUMBER]: "Backer Number",
  [OrderImportField.ITEM_UNIT_PRICE]: "Product column",
};
